import * as React from "react";

import { MaintenanceModal } from "./maintenance-modal";

/**
 * メンテナンスモード判定 + bypass key の処理を行うゲート。
 *
 * - 環境変数 `VITE_MAINTENANCE_MODE` が `'on'` の場合のみメンテナンス扱い。
 * - URL クエリ `?bypass=chillnn-dev` で来たら localStorage に保存し、
 *   以降のアクセスでもバイパスを継続する（解除は localStorage の値を消す）。
 * - バックエンド呼び出しは止めないため `children` は常にレンダリングし、
 *   その上にモーダルだけ被せる。
 */

// 固定の bypass key。値・キー名ともにソース直書きで運用する。
const BYPASS_QUERY_PARAM = "bypass";
const BYPASS_STORAGE_KEY = "maintenance_bypass";
const BYPASS_VALUE = "chillnn-dev";

const isMaintenanceModeOn = () => import.meta.env.VITE_MAINTENANCE_MODE === "on";

/**
 * URL クエリに bypass キーが付いていれば localStorage に保存する。
 * 既に保存済みかどうかと、現在 localStorage が一致しているかを返す。
 */
const resolveBypass = (): boolean => {
	if (typeof window === "undefined") return false;

	try {
		const params = new URLSearchParams(window.location.search);
		const queryValue = params.get(BYPASS_QUERY_PARAM);
		if (queryValue === BYPASS_VALUE) {
			window.localStorage.setItem(BYPASS_STORAGE_KEY, BYPASS_VALUE);
		}
		return window.localStorage.getItem(BYPASS_STORAGE_KEY) === BYPASS_VALUE;
	} catch {
		// localStorage が利用できない環境（プライベートブラウジング等）では bypass しない
		return false;
	}
};

export const MaintenanceGate = ({ children }: { children: React.ReactNode }) => {
	// メンテモードが off ならば一切何もせず通過
	const maintenanceOn = isMaintenanceModeOn();

	// bypass 判定はマウント時に一度だけ評価（メンテ中以外は実行しない）
	const bypassed = React.useMemo(() => (maintenanceOn ? resolveBypass() : false), [maintenanceOn]);

	const showModal = maintenanceOn && !bypassed;

	return (
		<>
			{children}
			{showModal && <MaintenanceModal />}
		</>
	);
};
